import { Users } from 'lucide-react';
// TODO(CT-5662): re-add these icons together with the commented-out Settings items below.
// import { Bell, CheckSquare, CreditCard, FolderOpen, Key, LayoutGrid, Monitor, Plug, Settings,
//   Settings2, Shield, ShoppingBag, User } from 'lucide-react';

/**
 * @typedef {{ id: string, name: string, description: string, icon: import('lucide-react').LucideIcon }} SettingsItem
 * @typedef {{ id: string, label: string, items: SettingsItem[], notice?: string }} SettingsSection
 *
 * NOTE: `name`, `description`, `label`, and `notice` here are **i18n keys** (e.g.
 * `'settings.sections.personal.label'`), not localized strings. The consumer must
 * pass a `t(key)` function to {@link filterSettingsSections} (and to
 * `SETTINGS_SECTIONS.map(...)`) to resolve them. Storybook and the shell both
 * follow this contract.
 */

/**
 * Default sections + items. Import this in your app shell, filter by role, then
 * pass the result to `<TopbarSettingsMenu sections={...} />`.
 *
 * @type {SettingsSection[]}
 */
export const SETTINGS_SECTIONS = [
  // TODO(CT-5662): hidden until their screens exist — People & Access is the only Settings entry for now.
  // {
  //   id: 'personal',
  //   labelKey: 'settings.sections.personal.label',
  //   items: [
  //     {
  //       id: 'profile',
  //       nameKey: 'settings.items.profile.name',
  //       descriptionKey: 'settings.items.profile.description',
  //       icon: User,
  //     },
  //     {
  //       id: 'notifications',
  //       nameKey: 'settings.items.notifications.name',
  //       descriptionKey: 'settings.items.notifications.description',
  //       icon: Bell,
  //     },
  //     {
  //       id: 'active-sessions',
  //       nameKey: 'settings.items.activeSessions.name',
  //       descriptionKey: 'settings.items.activeSessions.description',
  //       icon: Monitor,
  //     },
  //     {
  //       id: 'account-security',
  //       nameKey: 'settings.items.accountSecurity.name',
  //       descriptionKey: 'settings.items.accountSecurity.description',
  //       icon: Shield,
  //     },
  //     {
  //       id: 'api-keys',
  //       nameKey: 'settings.items.apiKeys.name',
  //       descriptionKey: 'settings.items.apiKeys.description',
  //       icon: Key,
  //     },
  //   ],
  // },
  // {
  //   id: 'agent-studio',
  //   labelKey: 'settings.sections.agentStudio.label',
  //   items: [
  //     {
  //       id: 'system',
  //       nameKey: 'settings.items.system.name',
  //       descriptionKey: 'settings.items.system.description',
  //       icon: Settings2,
  //     },
  //     {
  //       id: 'apps',
  //       nameKey: 'settings.items.apps.name',
  //       descriptionKey: 'settings.items.apps.description',
  //       icon: LayoutGrid,
  //     },
  //     {
  //       id: 'spaces',
  //       nameKey: 'settings.items.spaces.name',
  //       descriptionKey: 'settings.items.spaces.description',
  //       icon: FolderOpen,
  //     },
  //     {
  //       id: 'work-items',
  //       nameKey: 'settings.items.workItems.name',
  //       descriptionKey: 'settings.items.workItems.description',
  //       icon: CheckSquare,
  //     },
  //     {
  //       id: 'marketplace',
  //       nameKey: 'settings.items.marketplace.name',
  //       descriptionKey: 'settings.items.marketplace.description',
  //       icon: ShoppingBag,
  //     },
  //   ],
  // },
  {
    id: 'workspace',
    labelKey: 'settings.sections.workspace.label',
    items: [
      // {
      //   id: 'workspace-settings',
      //   nameKey: 'settings.items.workspaceSettings.name',
      //   descriptionKey: 'settings.items.workspaceSettings.description',
      //   icon: Settings,
      // },
      {
        id: 'people-access',
        nameKey: 'settings.items.peopleAccess.name',
        descriptionKey: 'settings.items.peopleAccess.description',
        icon: Users,
      },
      // {
      //   id: 'billing',
      //   nameKey: 'settings.items.billing.name',
      //   descriptionKey: 'settings.items.billing.description',
      //   icon: CreditCard,
      // },
      // {
      //   id: 'integrations',
      //   nameKey: 'settings.items.integrations.name',
      //   descriptionKey: 'settings.items.integrations.description',
      //   icon: Plug,
      // },
    ],
  },
];

/**
 * English fallbacks — used when no `t` is provided (e.g. Storybook without
 * an i18n provider) or when a key is missing from the active locale.
 * @type {Record<string, string>}
 */
export const SETTINGS_I18N_FALLBACKS = Object.freeze({
  'settings.sections.personal.label': 'PERSONAL SETTINGS',
  'settings.sections.agentStudio.label': 'AGENT STUDIO SETTINGS',
  'settings.sections.workspace.label': 'WORKSPACE SETTINGS',
  'settings.items.profile.name': 'Profile',
  'settings.items.profile.description': 'Name, photo, time zone, and basic account info',
  'settings.items.notifications.name': 'Notifications',
  'settings.items.notifications.description': 'Email and in-app notification preferences',
  'settings.items.activeSessions.name': 'Active sessions',
  'settings.items.activeSessions.description': 'Devices currently signed into your account',
  'settings.items.accountSecurity.name': 'Account & security',
  'settings.items.accountSecurity.description':
    'Login method, two-factor auth, and recovery',
  'settings.items.apiKeys.name': 'API keys & tokens',
  'settings.items.apiKeys.description':
    'Personal access tokens for the AIMS-OS API and SDKs',
  'settings.items.system.name': 'System',
  'settings.items.system.description':
    'General settings, security, and automation rules',
  'settings.items.apps.name': 'Apps',
  'settings.items.apps.description': 'Manage apps installed in this studio',
  'settings.items.spaces.name': 'Spaces',
  'settings.items.spaces.description': 'Categories, defaults, and archive policy',
  'settings.items.workItems.name': 'Work items',
  'settings.items.workItems.description': 'Types, workflows, and custom fields',
  'settings.items.marketplace.name': 'Marketplace',
  'settings.items.marketplace.description': 'Explore and install integrations',
  'settings.items.workspaceSettings.name': 'Workspace settings',
  'settings.items.workspaceSettings.description':
    'Name, defaults, retention, branding, and security policies',
  'settings.items.peopleAccess.name': 'People & Access',
  'settings.items.peopleAccess.description':
    'Invite people, manage roles and groups, and review pending invitations',
  'settings.items.billing.name': 'Billing & plan',
  'settings.items.billing.description':
    'Subscription, usage, invoices, and payment method',
  'settings.items.integrations.name': 'Integrations',
  'settings.items.integrations.description':
    'Salesforce, Slack, Zendesk, Twilio, and 30+ more',
  'settings.workspace.notice':
    "You don't have admin permissions to manage workspace settings.",
});

/**
 * @param {string} key
 * @param {((key: string) => string) | undefined} t
 * @returns {string}
 */
function resolve(key, t) {
  if (t) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  return SETTINGS_I18N_FALLBACKS[key] ?? key;
}

/**
 * Resolves i18n keys on a single section (mutates a shallow copy).
 * @param {SettingsSection} section
 * @param {((key: string) => string) | undefined} t
 * @returns {SettingsSection}
 */
function localizeSection(section, t) {
  const label = resolve(section.labelKey, t);
  const items = section.items.map((item) => ({
    id: item.id,
    icon: item.icon,
    name: resolve(item.nameKey, t),
    description: resolve(item.descriptionKey, t),
  }));
  const notice = section.notice
    ? resolve('settings.workspace.notice', t)
    : undefined;
  return { ...section, label, items, ...(notice ? { notice } : {}) };
}

/**
 * Convenience helper — applies role-based filtering AND i18n resolution to
 * SETTINGS_SECTIONS before passing them to `<TopbarSettingsMenu />`.
 *
 * @param {{ isAdmin?: boolean, hasWorkspaceAccess?: boolean, t?: (key: string) => string }} [options]
 * @returns {SettingsSection[]}
 */
export function filterSettingsSections({ isAdmin = false, hasWorkspaceAccess = true, t } = {}) {
  if (isAdmin && hasWorkspaceAccess) {
    // Sections whose items are all commented out (CT-5662) must not render an empty header.
    return SETTINGS_SECTIONS.filter((section) => section.items.length > 0).map((section) =>
      localizeSection(section, t),
    );
  }

  return SETTINGS_SECTIONS
    .map((section) => {
      if (section.id !== 'workspace') return localizeSection(section, t);

      if (!hasWorkspaceAccess) {
        return {
          ...localizeSection({ ...section, items: [] }, t),
          items: [],
          notice: resolve('settings.workspace.notice', t),
        };
      }

      return {
        ...localizeSection(
          {
            ...section,
            items: section.items.filter(
              (item) =>
                item.id !== 'workspace-settings' && item.id !== 'people-access',
            ),
          },
          t,
        ),
      };
    })
    .filter((section) => section.items.length > 0 || section.notice);
}
